/**
 * scroll-reveal.js — IntersectionObserver Entrance Animations
 *
 * Finds elements with [data-reveal] inside a .js-scroll-reveal root,
 * observes them with IntersectionObserver, and adds .is-revealed when
 * they enter the viewport. CSS handles the actual animation.
 *
 * Features:
 *   - Stagger support via [data-reveal-stagger] containers
 *   - Respects prefers-reduced-motion
 *   - Falls back gracefully if IO is unsupported
 *   - No scroll listeners — purely IO + CSS
 */

const REVEAL_ATTR = "data-reveal";
const STAGGER_ATTR = "data-reveal-stagger";
const MANAGED_CLASS = "sr-managed";
const REVEALED_CLASS = "is-revealed";

export function initScrollReveal() {
  const root = document.querySelector(".js-scroll-reveal");
  if (!root) return;

  const prefersReduced =
    window.matchMedia &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  const revealEls = Array.from(root.querySelectorAll(`[${REVEAL_ATTR}]`));
  if (!revealEls.length) return;

  // Mark all elements as JS-managed (prevents FOUC — CSS hides these)
  revealEls.forEach((el) => el.classList.add(MANAGED_CLASS));

  // Assign stagger indices inside stagger containers
  const containers = root.querySelectorAll(`[${STAGGER_ATTR}]`);
  containers.forEach((container) => {
    const selector = container.getAttribute(STAGGER_ATTR) || `[${REVEAL_ATTR}]`;
    const items = container.querySelectorAll(selector);
    let index = 0;

    items.forEach((el) => {
      if (!el.classList.contains(MANAGED_CLASS)) return;
      el.style.setProperty("--reveal-stagger-index", index++);
    });
  });

  // Reduced motion: reveal everything immediately
  if (prefersReduced) {
    revealEls.forEach((el) => el.classList.add(REVEALED_CLASS));
    return;
  }

  // No IntersectionObserver support: reveal everything immediately
  if (!("IntersectionObserver" in window)) {
    revealEls.forEach((el) => el.classList.add(REVEALED_CLASS));
    return;
  }

  // Observe elements and reveal on intersection
  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add(REVEALED_CLASS);
        observer.unobserve(entry.target);
      });
    },
    {
      root: null,
      rootMargin: "0px 0px -10% 0px",
      threshold: 0.2,
    },
  );

  revealEls.forEach((el) => observer.observe(el));
}
